/* eslint-disable react-refresh/only-export-components -- intentionally exports a hook alongside its provider/components */
import { useState, useRef, useEffect } from "react";

/**
 * Hook: smoothly animates a number from its previous value to the new `target`.
 * Uses an ease-out cubic curve over `duration` ms.
 */
export function useAnimatedValue(target: number, duration = 600): number {
  const [display, setDisplay] = useState(target);
  const prevRef = useRef(target);
  const rafRef  = useRef(0);

  useEffect(() => {
    if (isNaN(target)) return;
    if (target === prevRef.current) return;
    const from = isNaN(prevRef.current) ? 0 : prevRef.current;
    prevRef.current = target;
    const start = performance.now();
    cancelAnimationFrame(rafRef.current);

    function step(now: number) {
      const elapsed = Math.min((now - start) / duration, 1);
      const eased   = 1 - Math.pow(1 - elapsed, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (elapsed < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    }

    rafRef.current = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return isNaN(display) ? 0 : display;
}

interface AnimatedTotalProps {
  value: number;
  prefix?: string;
  suffix?: string;
  className?: string;
}

/**
 * Component: renders an animated number that ticks from old to new value.
 */
export function AnimatedTotal({ value, prefix = "", suffix = "", className = "" }: AnimatedTotalProps) {
  const animated = useAnimatedValue(value);
  return <span className={className}>{prefix}{animated}{suffix}</span>;
}

interface AnimatedNumProps {
  value: number;
  decimals?: number;
}

/**
 * Component: like AnimatedTotal but supports decimal places (for averages).
 */
export function AnimatedNum({ value, decimals = 0 }: AnimatedNumProps) {
  const factor   = Math.pow(10, decimals);
  const animated = useAnimatedValue(Math.round(value * factor));
  return <>{decimals > 0 ? (animated / factor).toFixed(decimals) : animated}</>;
}
